import { performDirectTokenCookieExchange } from "./partnerEndpoints";
import config from "./config";

/**
 * WebView bootstrap that accepts a JWT from the native shell, trades it for a cookie via
 * /v1/pixels/partners/entry_direct, and leaves the cookie in the WebView storage so the SPA can continue.
 *
 * The native host must dispatch `window.NativeBridge.emitToWeb("native-jwt", payload)` or push the
 * same payload into `window.__jwtQueue` before the bundle is ready. The payload must include the raw
 * JWT (no `Bearer` prefix) plus the resource link + organization identifiers so we can build the
 * entry URL when exchanging the token.
 */

type WindowWithQueue = Window & {
  __jwtQueue?: NativeJwtPayload[];
};

type NativeJwtPayload = {
  token: string;
  resLinkParamsId: string;
  organizationId: string;
};

const windowWithQueue = window as WindowWithQueue;

// Track payloads we've already exchanged so native can't accidentally trigger multiple POSTs.
const processedTokens = new Set<string>();

// Core flow: POST the JWT to /entry, accept the redirect response, and rely on Set-Cookie for auth.
export const exchangeToken = async (token: string, resLinkParamsId: string, organizationId: string): Promise<void> => {
  const dedupeKey = `${organizationId}:${resLinkParamsId}:${token}`;
  if (processedTokens.has(dedupeKey)) {
    return;
  }
  processedTokens.add(dedupeKey);
  
  try {
    await performDirectTokenCookieExchange(resLinkParamsId, organizationId, token);
    console.log("[nativeJwtExchange] JWT exchange succeeded.");

    const navigationUrl = `/partner?res_link_params_id=${encodeURIComponent(resLinkParamsId)}&org_id=${encodeURIComponent(organizationId)}`;
    console.log(`[nativeJwtExchange] Dispatching partner-authenticated for ${navigationUrl}`);

    // This event is caught in App.tsx to navigate to the partner entry point.
    // We cannot navigate directly here because we are outside React context.
    window.dispatchEvent(
      new CustomEvent("partner-authenticated", {
        detail: { path: navigationUrl },
      })
    );
  } catch (err) {
    processedTokens.delete(dedupeKey);
    console.error("[nativeJwtExchange] JWT exchange error:", err);
  }
};

// Guard the listener so we only accept the expected payload shape and surface useful logging otherwise.
const handleDetail = (detail: unknown) => {
  if (!detail || typeof detail !== "object") {
    console.error("[nativeJwtExchange] Expected an object payload from native, received:", detail);
    return;
  }

  const payload = detail as Partial<NativeJwtPayload>;

  if (
    typeof payload.token !== "string" ||
    typeof payload.resLinkParamsId !== "string" ||
    typeof payload.organizationId !== "string"
  ) {
    console.error("[nativeJwtExchange] Received payload missing required fields:", detail);
    return;
  }

  const token = payload.token.trim();
  const resLinkParamsId = payload.resLinkParamsId.trim();
  const organizationId = payload.organizationId.trim();

  if (!token || !resLinkParamsId || !organizationId) {
    console.error(
      "[nativeJwtExchange] Missing token, resLinkParamsId, or organizationId in payload. Aborting exchange."
    );
    return;
  }

  // Just fire and forget. We don't need to await this.
  void exchangeToken(token, resLinkParamsId, organizationId);
};

// If native injected tokens before the bundle mounted, drain them now so nothing is lost.
const drainQueue = () => {
  const queue = windowWithQueue.__jwtQueue;
  if (!Array.isArray(queue) || queue.length === 0) {
    return;
  }
  console.log(`[nativeJwtExchange] Draining ${queue.length} queued JWT payload(s).`);
  queue.splice(0).forEach((payload) => handleDetail(payload));
};

if (config.enableNativeBridge) {
  // Listen for real-time events fired via window.NativeBridge.emitToWeb("native-jwt", payload).
  window.addEventListener("native-jwt", (event: Event) => {
    const customEvent = event as CustomEvent<unknown>;
    handleDetail(customEvent.detail);
  });

  // Run once on bootstrap to grab anything the native layer queued before React took over.
  drainQueue();
}
